import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Native horizontal event rail used by Home and Browse.
 * Supports touch/trackpad scrolling, mouse dragging, keyboard focus, and
 * arrow-button paging while keeping the browser scrollbar hidden.
 */
const EventRow = ({ title, events = [] }) => {
  const navigate = useNavigate();
  const viewportRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startScrollLeft: 0, moved: false });
  const suppressClickRef = useRef(false);
  const [activeId, setActiveId] = useState(null);
  const [hasPointerFine, setHasPointerFine] = useState(true);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setHasPointerFine(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update);
    };
  }, []);

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    setCanPrev(viewport.scrollLeft > 2);
    setCanNext(viewport.scrollLeft < maxScrollLeft - 2);
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const frame = requestAnimationFrame(updateScrollState);
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updateScrollState)
      : null;
    observer?.observe(viewport);
    window.addEventListener("resize", updateScrollState);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [events, updateScrollState]);

  const scrollByPage = (direction) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollBy({
      left: direction * viewport.clientWidth * 0.85,
      behavior: "smooth",
    });
  };

  const handlePointerDown = (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startScrollLeft: viewport.scrollLeft,
      moved: false,
    };
  };

  const handlePointerMove = (e) => {
    const viewport = viewportRef.current;
    const drag = dragRef.current;
    if (!viewport || !drag.active) return;
    const delta = e.clientX - drag.startX;
    if (Math.abs(delta) > 5 && !drag.moved) {
      drag.moved = true;
      setIsDragging(true);
      viewport.setPointerCapture?.(e.pointerId);
    }
    if (!drag.moved) return;
    e.preventDefault();
    viewport.scrollLeft = drag.startScrollLeft - delta;
    updateScrollState();
  };

  const finishPointerDrag = (e) => {
    const viewport = viewportRef.current;
    const dragged = dragRef.current.moved;
    dragRef.current.active = false;
    setIsDragging(false);
    if (viewport?.hasPointerCapture?.(e.pointerId)) {
      viewport.releasePointerCapture(e.pointerId);
    }
    if (dragged) {
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    }
  };

  const openEvent = (id) => navigate(`/event/${id}`);

  const handleCardClick = (event) => {
    if (suppressClickRef.current) return;
    openEvent(event.id);
  };

  const handleKeyDown = (e, event) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openEvent(event.id);
    }
  };

  if (!events.length) return null;

  return (
    <section className="mb-10 group/row" data-testid={`event-row-${title}`}>
      {title && (
        <h2 className="text-white text-2xl font-bold px-4 sm:px-6 lg:px-8 mb-4">{title}</h2>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => scrollByPage(-1)}
          aria-label="Scroll event flyers left"
          className={`hidden sm:flex items-center justify-center absolute left-0 top-0 bottom-0 z-30 w-12
            bg-gradient-to-r from-black/70 to-transparent text-white transition-opacity duration-200
            ${canPrev ? "opacity-0 group-hover/row:opacity-100 focus:opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ChevronLeft className="w-8 h-8" />
        </button>

        <div
          ref={viewportRef}
          className={`hide-scrollbar overflow-x-auto overscroll-x-contain scroll-smooth py-10 px-4 sm:px-6 lg:px-8 ${isDragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
          style={{ scrollSnapType: "x proximity" }}
          onScroll={updateScrollState}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishPointerDrag}
          onPointerCancel={finishPointerDrag}
          data-testid={`event-row-scroll-${title}`}
        >
          <div className="flex w-max gap-4">
            {events.map((event) => {
              const active = activeId === event.id;
              const isLive = event.status === "live";
              return (
                <div
                  key={event.id}
                  role="button"
                  tabIndex={0}
                  data-testid={`event-card-${event.id}`}
                  onClick={() => handleCardClick(event)}
                  onKeyDown={(e) => handleKeyDown(e, event)}
                  onMouseEnter={() => hasPointerFine && !isDragging && setActiveId(event.id)}
                  onMouseLeave={() => hasPointerFine && setActiveId(null)}
                  onFocus={() => setActiveId(event.id)}
                  onBlur={() => setActiveId(null)}
                  className={`relative shrink-0 w-[180px] sm:w-[200px] aspect-[3/4] rounded-lg cursor-pointer outline-none snap-start
                    bg-[#0b0d12] ring-1 ring-white/10 transition-[transform,box-shadow] duration-300 ease-out
                    ${active
                      ? isLive
                        ? "scale-[1.03] z-20 shadow-[0_18px_45px_rgba(0,0,0,0.65),0_0_42px_rgba(239,68,68,0.9)] ring-red-400/80"
                        : "scale-[1.03] z-20 shadow-[0_18px_45px_rgba(0,0,0,0.65),0_0_35px_rgba(59,130,246,0.65)] ring-blue-400/60"
                      : isLive
                        ? "scale-100 z-0 ring-red-500/70 shadow-[0_0_28px_rgba(239,68,68,0.65)]"
                        : "scale-100 z-0"}`}
                >
                  <img
                    src={event.image_url}
                    alt={event.title}
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                    className={`w-full h-full rounded-lg transition-[object-fit] duration-300 ${active ? "object-contain" : "object-cover"}`}
                  />

                  {isLive && (
                    <span className="absolute left-2 top-2 z-10 rounded-full border border-red-300/40 bg-red-600 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-[0_0_18px_rgba(239,68,68,.8)]">
                      Live Now
                    </span>
                  )}

                  <div
                    className={`absolute inset-x-0 bottom-0 p-4 rounded-b-lg bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-300 ${active ? "opacity-100" : "opacity-0"}`}
                  >
                    <p className="text-white font-bold leading-tight line-clamp-2">{event.title}</p>
                    {event.category && <p className="text-blue-400 text-sm mt-1">{event.category}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => scrollByPage(1)}
          aria-label="Scroll event flyers right"
          className={`hidden sm:flex items-center justify-center absolute right-0 top-0 bottom-0 z-30 w-12
            bg-gradient-to-l from-black/70 to-transparent text-white transition-opacity duration-200
            ${canNext ? "opacity-0 group-hover/row:opacity-100 focus:opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      </div>
    </section>
  );
};

export default EventRow;
